import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router';
import type { LogisticsCalculationListItem } from '@magimp/shared';
import {
  Button,
  DataTable,
  PageHeader,
  WorkspaceTable,
  useInteractiveRow,
  useNarrow,
  type Column,
  type WorkspaceTableColumn,
} from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';
import type { AppLocale } from '../../../i18n/index.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useLogisticsCalculationsList } from '../queries.js';
import { LogisticsCalculationStatusBadge } from './LogisticsCalculationStatusBadge.js';

export function LogisticsCalculationsListPage() {
  const { t, i18n } = useTranslation('logisticsCalculations');
  const narrow = useNarrow();
  const navigate = useNavigate();
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = hasPermission(permissions, 'logisticsCalculations.manage');
  const query = useLogisticsCalculationsList();
  const columns = useMemo<Column<LogisticsCalculationListItem>[]>(
    () => listColumns(t, i18n.language),
    [i18n.language, t],
  );
  return (
    <div className="rl-list-page" data-testid="rl-list-page">
      <PageHeader
        title={t('title')}
        narrow={narrow}
        actions={
          canManage ? (
            <Button kind="primary" icon="plus" onClick={() => navigate('/ttn/new')}>
              {t('actions.create')}
            </Button>
          ) : null
        }
      />
      <div className="rl-list-body">
        {query.isLoading ? <p className="rl-list-state">{t('loading')}</p> : null}
        {query.isError ? (
          <p className="rl-list-state" role="alert">
            {t('error')}
          </p>
        ) : null}
        {query.data && query.data.items.length === 0 ? (
          <p className="rl-list-state">{t('empty')}</p>
        ) : null}
        {query.data && query.data.items.length > 0 && narrow ? (
          <DataTable
            columns={columns}
            rows={query.data.items}
            onSelect={(row) => navigate(`/ttn/${encodeURIComponent(row.id)}`)}
            initialSort={{ key: 'updatedAt', dir: 'desc' }}
            renderMobileCard={({ row, onSelect }) => (
              <LogisticsCalculationMobileCard
                row={row}
                locale={i18n.language as AppLocale}
                onSelect={() => onSelect?.(row)}
              />
            )}
          />
        ) : null}
        {query.data && query.data.items.length > 0 && !narrow ? (
          <LogisticsCalculationsListTable
            rows={query.data.items}
            locale={i18n.language as AppLocale}
            onOpenRow={(id) => navigate(`/ttn/${encodeURIComponent(id)}`)}
          />
        ) : null}
      </div>
    </div>
  );
}

const GRID_TEMPLATE =
  '110px 120px minmax(160px,1fr) minmax(140px,1fr) minmax(140px,1fr) 100px 110px 90px 160px';

/**
 * What the cargo column says: the codes of the cargos the document carries.
 *
 * A row from a tab loaded before the list carried them has only the count, and
 * says that instead of nothing.
 */
export function cargoArticlesText(row: LogisticsCalculationListItem): string {
  return row.cargoArticles ? row.cargoArticles.join(', ') : String(row.cargoCount);
}

export function LogisticsCalculationsListTable({
  rows,
  locale,
  onOpenRow,
}: {
  rows: readonly LogisticsCalculationListItem[];
  locale: AppLocale;
  onOpenRow: (id: string) => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const columns: readonly WorkspaceTableColumn[] = [
    { key: 'publicCode', label: t('list.number') },
    { key: 'status', label: t('list.status') },
    { key: 'clientCompany', label: t('list.client') },
    { key: 'ownerName', label: t('list.owner') },
    { key: 'cargoCount', label: t('list.cargos') },
    { key: 'totalUsd', label: t('list.totalUsd'), align: 'right' },
    { key: 'totalRub', label: t('list.totalRub'), align: 'right' },
    { key: 'htmlViewCount', label: t('list.views'), align: 'right' },
    { key: 'updatedAt', label: t('list.updated') },
  ];
  return (
    <WorkspaceTable columnsTemplate={GRID_TEMPLATE} columns={columns}>
      {rows.map((row) => (
        <LogisticsCalculationListRow key={row.id} row={row} locale={locale} onOpenRow={onOpenRow} />
      ))}
    </WorkspaceTable>
  );
}

function LogisticsCalculationListRow({
  row,
  locale,
  onOpenRow,
}: {
  row: LogisticsCalculationListItem;
  locale: AppLocale;
  onOpenRow: (id: string) => void;
}) {
  const { background, handlers } = useInteractiveRow({
    selected: false,
    onActivate: () => onOpenRow(row.id),
  });
  const cell = (extra: React.CSSProperties = {}): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    height: 44,
    minWidth: 0,
    padding: '0 10px',
    color: 'var(--fg-1)',
    fontSize: 13,
    borderBottom: '1px solid var(--border-1)',
    background,
    cursor: 'pointer',
    transition: 'background var(--dur-1) var(--ease-out)',
    ...extra,
  });
  const numeric = { justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' } as const;
  return (
    <>
      <div
        {...handlers}
        data-testid={`rl-list-row-${row.id}`}
        style={cell({ fontVariantNumeric: 'tabular-nums' })}
      >
        {row.publicCode}
      </div>
      <div {...handlers} style={cell()}>
        <LogisticsCalculationStatusBadge status={row.status} size="sm" />
      </div>
      <div {...handlers} style={cell({ overflow: 'hidden', textOverflow: 'ellipsis' })}>
        {row.clientCompany}
      </div>
      <div {...handlers} style={cell({ overflow: 'hidden', textOverflow: 'ellipsis' })}>
        {row.ownerName}
      </div>
      <div
        {...handlers}
        title={cargoArticlesText(row)}
        style={cell({ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}
      >
        {cargoArticlesText(row)}
      </div>
      <div {...handlers} style={cell(numeric)}>
        {row.totalUsd === null ? '—' : `${row.totalUsd} $`}
      </div>
      <div {...handlers} style={cell(numeric)}>
        {row.totalRub === null ? '—' : `${row.totalRub} ₽`}
      </div>
      <div {...handlers} style={cell(numeric)}>
        {row.htmlViewCount}
      </div>
      <div {...handlers} style={cell({ color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' })}>
        {formatDateTime(row.updatedAt, locale)}
      </div>
    </>
  );
}

function listColumns(
  t: TFunction<'logisticsCalculations'>,
  locale: string,
): Column<LogisticsCalculationListItem>[] {
  return [
    { key: 'publicCode', label: t('list.number'), sortable: true },
    {
      key: 'statusDisplay',
      label: t('list.status'),
      render: (row) => <LogisticsCalculationStatusBadge status={row.status} size="sm" />,
    },
    { key: 'clientCompany', label: t('list.client'), sortable: true },
    { key: 'ownerName', label: t('list.owner'), sortable: true },
    {
      key: 'cargoCount',
      label: t('list.cargos'),
      sortable: true,
      // Sorted by how many cargos a document carries, not by the codes it lists.
      sortValue: (row) => row.cargoCount,
      render: (row) => (
        <span className="rl-list-cargos" title={cargoArticlesText(row)}>
          {cargoArticlesText(row)}
        </span>
      ),
    },
    {
      key: 'totalUsd',
      label: t('list.totalUsd'),
      align: 'right',
      render: (row) => (row.totalUsd === null ? '—' : `${row.totalUsd} $`),
    },
    {
      key: 'totalRub',
      label: t('list.totalRub'),
      align: 'right',
      render: (row) => (row.totalRub === null ? '—' : `${row.totalRub} ₽`),
    },
    { key: 'htmlViewCount', label: t('list.views'), align: 'right', sortable: true },
    {
      key: 'updatedAt',
      label: t('list.updated'),
      sortable: true,
      sortValue: (row) => row.updatedAt,
      render: (row) => formatDateTime(row.updatedAt, locale as AppLocale),
    },
  ];
}

export function LogisticsCalculationMobileCard({
  row,
  locale,
  onSelect,
}: {
  row: LogisticsCalculationListItem;
  locale: AppLocale;
  onSelect: () => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  return (
    <button className="rl-list-card" type="button" onClick={onSelect}>
      <span className="rl-list-card-head">
        <strong>{row.publicCode}</strong>
        <LogisticsCalculationStatusBadge status={row.status} size="sm" />
      </span>
      <span>{row.clientCompany || '—'}</span>
      <span>
        {t('list.owner')}: {row.ownerName}
      </span>
      <span className="rl-list-cargos" title={cargoArticlesText(row)}>
        {t('list.cargos')}: {cargoArticlesText(row)}
      </span>
      <span className="rl-list-card-totals">
        {row.totalUsd ?? '—'} $ · {row.totalRub ?? '—'} ₽
      </span>
      <span>
        {t('list.views')}: {row.htmlViewCount}
      </span>
      <span>
        {t('list.updated')}: {formatDateTime(row.updatedAt, locale)}
      </span>
    </button>
  );
}
